import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newsArticles } from "@/mocks/newsArticles";
import { newsCategories } from "@/mocks/newsCategories";
import type { NewsArticle } from "@/types/news";
import { formatHeat } from "@/utils/news";

const filters = [{ slug: "all", name: "全部" }, ...newsCategories.map((c) => ({ slug: c.slug, name: c.name }))];

function LiveFeed() {
  const [active, setActive] = useState("all");
  const [visible, setVisible] = useState(4);

  const list = useMemo<NewsArticle[]>(() => {
    if (active === "all") {
      return newsArticles;
    }
    return newsArticles.filter((item) => item.category === active);
  }, [active]);

  const shown = list.slice(0, visible);

  const handleFilter = (slug: string) => {
    setActive(slug);
    setVisible(4);
  };

  return (
    <section id="live-feed" className="w-full px-4 md:px-10 py-14 md:py-20 bg-background-50">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-medium text-primary-600 tracking-[0.2em]">
            <span className="w-6 h-px bg-primary-500" />
            LIVE FEED
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-black text-foreground-950">
            快讯时间流 · 最新进展抢先看
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            按发布时间倒序同步，突发、回应、后续动态都在这条线上。点开标签可以只看某一类瓜。
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {filters.map((filter) => (
            <button
              key={filter.slug}
              type="button"
              onClick={() => handleFilter(filter.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                active === filter.slug
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {filter.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {shown.map((article) => (
          <article
            key={article.id}
            className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden flex flex-col transition-colors hover:border-primary-300"
          >
            <div className="relative w-full h-48 overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 瓜圈速报`}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3 flex items-center gap-2">
                {article.isBreaking && (
                  <span className="px-2 py-1 rounded-md bg-primary-500 text-background-50 text-[11px] font-bold">
                    突发
                  </span>
                )}
                <span className="px-2 py-1 rounded-md bg-foreground-950/70 text-background-50 text-[11px] font-medium">
                  {article.categoryName}
                </span>
              </div>
              <span className="absolute bottom-3 right-3 px-2 py-1 rounded-md bg-background-50/90 text-foreground-800 text-[11px] font-medium">
                {article.publishedAt}
              </span>
            </div>

            <div className="flex-1 p-5 flex flex-col">
              <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
                <Link to={`/article/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {article.title}
                </Link>
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 line-clamp-3">{article.summary}</p>

              <div className="mt-auto pt-5 flex items-center justify-between">
                <Link
                  to={`/article/${article.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  看这条速报
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <span className="w-3.5 h-3.5 flex items-center justify-center text-primary-500">
                    <i className="ri-fire-line" />
                  </span>
                  {formatHeat(article.heat)}
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>

      {shown.length === 0 && (
        <p className="py-16 text-center text-sm text-foreground-500">这一类暂时还没有新速报，换个标签看看。</p>
      )}

      {visible < list.length && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((prev) => prev + 3)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-600 transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-down-line" />
            </span>
            加载更多速报（还有 {list.length - visible} 条）
          </button>
        </div>
      )}
    </section>
  );
}

export default LiveFeed;